import {
  DeesElement,
  property,
  html,
  css,
  type CSSResult,
  cssManager,
} from '@design.estate/dees-element';
import * as domtools from '@design.estate/dees-domtools';
import { themeDefaultStyles } from '../../00theme.js';
import '../../00group-layout/dees-label/dees-label.js';

/**
 * Base class for all dees-input components
 * Provides unified margin system and layout mode support
 */
export abstract class DeesInputBase<T = any> extends DeesElement {
  // Keep declarations tied to the superclass's DOM contract instead of emitting
  // the build compiler's inferred (and potentially newer) ShadowRootInit shape.
  public static override shadowRootOptions: typeof DeesElement.shadowRootOptions = {
    ...DeesElement.shadowRootOptions,
    delegatesFocus: true,
  };
  public static inputLayout: 'field' | 'choice' | 'compound' = 'field';
  /**
   * Layout mode for the input component
   * - vertical: Traditional form layout (label on top)
   * - horizontal: Inline layout (label position configurable)
   * - auto: Detect from parent context
   */
  @property({ type: String })
  accessor layoutMode: 'vertical' | 'horizontal' | 'auto' = 'auto';

  /**
   * Position of the label relative to the input
   */
  @property({ type: String })
  accessor labelPosition: 'top' | 'left' | 'right' | 'none' = 'top';

  /**
   * Common properties for all inputs
   */
  @property({ type: String })
  accessor key!: string;

  @property({ type: String })
  accessor label!: string;

  @property({ type: Boolean, reflect: true })
  accessor required: boolean = false;

  @property({ type: Boolean, reflect: true })
  accessor disabled: boolean = false;

  @property({ type: String })
  accessor infoText!: string;

  @property({ type: String })
  accessor description!: string;

  /**
   * Common styles for all input components
   */
  public static get baseStyles(): CSSResult[] {
    return [
      themeDefaultStyles,
      css`
        /* CSS Variables for consistent spacing */
        :host {
          --dees-input-spacing-unit: var(--dees-spacing-sm);
          --dees-input-label-gap: 8px;
          --dees-input-height: var(--dees-input-control-height, var(--dees-control-height-lg));
          --dees-input-action-size: 28px;
          font-family: var(--dees-font-family);
          color: var(--dees-color-text-primary);
          min-width: 0;
        }

        /* Default block display with no margins - spacing is container-driven */
        :host {
          display: block;
          margin: 0;
        }

        /* Horizontal layout mode - activated by attribute */
        :host([layout-mode="horizontal"]) {
          display: inline-block;
        }

        /* Label position variations */
        :host([label-position="left"]) .input-wrapper {
          display: grid;
          grid-template-columns: minmax(0, var(--dees-input-label-width, 38%)) minmax(0, 1fr);
          gap: var(--dees-input-label-gap);
          align-items: center;
        }
        :host([label-position="left"]) .maincontainer,
        :host([label-position="left"]) .input-container {
          min-width: 0;
          width: 100%;
          max-width: var(--dees-input-control-max-width, none);
          justify-self: end;
        }
        :host([label-position="left"]) dees-label { align-self: center; --dees-label-gap: 0px; }
        :host([label-position="left"]) .descriptionText { grid-column: 1 / -1; }
        :host([input-layout="compound"]) .input-wrapper { display: block; }
        :host([input-layout="compound"]) dees-label { --dees-label-gap: 6px; }

        :host([label-position="right"]) .input-wrapper {
          display: grid;
          grid-template-columns: 1fr auto;
          gap: var(--dees-input-label-gap);
          align-items: center;
        }

        :host([label-position="top"]) .input-wrapper {
          display: block;
        }

        :host([label-position="none"]) dees-label {
          display: none;
        }

        dees-label { display: block; min-width: 0; }
        .input-wrapper { min-width: 0; }
        .field-control {
          box-sizing: border-box;
          min-width: 0;
          min-height: var(--dees-input-height);
          background: var(--dees-input-control-bg, var(--dees-color-control-bg));
          border: 1px solid var(--dees-input-control-border, var(--dees-color-control-border));
          border-radius: var(--dees-radius-lg);
          corner-shape: var(--dees-corner-shape);
          color: var(--dees-color-text-primary);
          font: var(--dees-font-control-size)/1.5 var(--dees-font-family);
          box-shadow: none;
          outline: none;
          transition: border-color var(--dees-transition-fast), background var(--dees-transition-fast);
        }
        @media (hover: hover) {
          :host(:not([disabled])) .field-control:hover:not(:focus-within) { border-color: var(--dees-color-border-strong); }
        }
        .field-control:focus-within {
          outline: 2px solid var(--dees-color-focus-ring);
          outline-offset: 1px;
          box-shadow: none;
        }
        :host([disabled]) .field-control {
          background: var(--dees-color-fill-tertiary);
          color: var(--dees-color-text-muted);
          cursor: not-allowed;
          opacity: 1;
        }
        .input-action {
          display: inline-flex; align-items: center; justify-content: center;
          flex-shrink: 0; min-width: var(--dees-input-action-size); min-height: var(--dees-input-action-size);
          padding: 4px; border: 0; border-radius: var(--dees-radius-sm);
          background: transparent; color: var(--dees-color-text-secondary); font: inherit; cursor: pointer;
        }
        .input-action:disabled { opacity: .4; cursor: default; }
        .input-action:focus-visible { outline: 2px solid var(--dees-color-focus-ring); outline-offset: -2px; }
        @media (hover: hover) { .input-action:not(:disabled):hover { background: var(--dees-color-hover); color: var(--dees-color-text-primary); } }
        @media (pointer: coarse) {
          :host { --dees-input-height: max(44px, var(--dees-input-control-height, 44px)); --dees-input-action-size: 44px; }
        }
        @media (prefers-reduced-motion: reduce) { .field-control, .input-action { transition: none; } }

        /* Description text below input */
        .descriptionText {
          margin-top: var(--dees-spacing-xs);
          font-size: var(--dees-font-footnote-size);
          line-height: var(--dees-font-footnote-line);
          color: var(--dees-color-text-secondary);
          overflow-wrap: anywhere;
        }
      `,
    ];
  }

  /**
   * Subject for value changes that all inputs should implement
   */
  public changeSubject = new domtools.plugins.smartrx.rxjs.Subject<T>();

  /**
   * Called when the element is connected to the DOM
   * Sets up layout mode detection
   */
  async connectedCallback() {
    await super.connectedCallback();
    this.setAttribute('input-layout', (this.constructor as typeof DeesInputBase).inputLayout);
    this.detectLayoutMode();
  }

  /**
   * Detects the appropriate layout mode based on parent context
   */
  private detectLayoutMode() {
    if (this.layoutMode !== 'auto') {
      this.setAttribute('layout-mode', this.layoutMode);
      return;
    }

    // Check if parent is a form with horizontal layout
    const parentForm = this.closest('dees-form');
    if (parentForm && parentForm.hasAttribute('horizontal-layout')) {
      this.setAttribute('layout-mode', 'horizontal');
    } else {
      this.setAttribute('layout-mode', 'vertical');
    }
  }

  /**
   * Updates the layout mode attribute when property changes
   */
  updated(changedProperties: Map<string, any>) {
    super.updated(changedProperties);
    
    if (changedProperties.has('layoutMode')) {
      this.detectLayoutMode();
    }

    if (changedProperties.has('labelPosition')) {
      this.setAttribute('label-position', this.labelPosition);
    }
  }

  /**
   * Standard method for freezing input (disabling)
   */
  public async freeze() {
    this.disabled = true;
  }

  /**
   * Standard method for unfreezing input (enabling)
   */
  public async unfreeze() {
    this.disabled = false;
  }

  /**
   * Renders the description text below the input.
   * Call ${this.renderDescription()} at the end of your render template.
   */
  public renderDescription() {
    return this.description ? html`<div class="descriptionText">${this.description}</div>` : '';
  }

  public renderLabel() {
    return html`<dees-label .label=${this.label} .infoText=${this.infoText} .required=${this.required}></dees-label>`;
  }

  /**
   * Abstract method that child classes must implement to get their value
   */
  public abstract getValue(): any;

  /**
   * Abstract method that child classes must implement to set their value
   */
  public abstract setValue(value: any): void;
}
